import React from 'react';
import { FileText, CreditCard } from 'lucide-react';

export const PaymentsTab = ({
  paymentHistory, paymentTotals, companies,
  payFilterCompany, setPayFilterCompany,
  payFilterDateDebut, setPayFilterDateDebut,
  payFilterDateFin, setPayFilterDateFin,
  fetchPaymentHistory, printInvoiceById, t
}) => (
  <div className="space-y-4" data-testid="b2b-payments-tab">
    {/* Filters */}
    <div className="flex flex-wrap gap-3 items-end">
      <div>
        <label className="block text-xs text-gray-400 mb-1">{t('adminB2b.filterCompany')}</label>
        <select value={payFilterCompany} onChange={e => setPayFilterCompany(e.target.value)}
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none min-w-[180px]"
          data-testid="pay-filter-company">
          <option value="">{t('adminB2b.all')}</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.company_name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1">{t('adminB2b.filterDateStart')}</label>
        <input type="date" value={payFilterDateDebut} onChange={e => setPayFilterDateDebut(e.target.value)}
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none"
          data-testid="pay-filter-date-debut" />
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1">{t('adminB2b.filterDateEnd')}</label>
        <input type="date" value={payFilterDateFin} onChange={e => setPayFilterDateFin(e.target.value)}
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none"
          data-testid="pay-filter-date-fin" />
      </div>
      <button onClick={fetchPaymentHistory}
        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-bold transition-colors"
        data-testid="pay-filter-apply">
        {t('adminB2b.filter')}
      </button>
      {(payFilterCompany || payFilterDateDebut || payFilterDateFin) && (
        <button onClick={() => { setPayFilterCompany(''); setPayFilterDateDebut(''); setPayFilterDateFin(''); setTimeout(fetchPaymentHistory, 100); }}
          className="px-4 py-2 bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10 rounded-xl text-sm transition-colors"
          data-testid="pay-filter-clear">
          {t('adminB2b.clearFilter')}
        </button>
      )}
    </div>

    {/* KPIs */}
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
        <p className="text-xs text-green-400/70 font-semibold uppercase">{t('adminB2b.totalCollected')}</p>
        <p className="text-2xl font-bold text-green-400">{paymentTotals.total_amount.toFixed(2)} &euro;</p>
        <p className="text-xs text-gray-500">{paymentHistory.length} {t('adminB2b.payments')}</p>
      </div>
      {Object.entries(paymentTotals.by_method).map(([method, amount]) => (
        <div key={method} className="bg-white/5 border border-white/10 rounded-xl p-4">
          <p className="text-xs text-gray-400 font-semibold uppercase">{method}</p>
          <p className="text-lg font-bold text-white">{amount.toFixed(2)} &euro;</p>
        </div>
      ))}
    </div>

    {/* Payment list */}
    <div className="space-y-2">
      <h3 className="text-white font-bold text-sm">{t('adminB2b.payHistoryTitle')}</h3>
      <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 text-xs text-gray-500 font-semibold uppercase tracking-wide">
        <div className="col-span-2">Date</div>
        <div className="col-span-3">Entreprise</div>
        <div className="col-span-2">Facture</div>
        <div className="col-span-2">Mode</div>
        <div className="col-span-1">Montant</div>
        <div className="col-span-2">Actions</div>
      </div>
      {paymentHistory.length === 0 ? (
        <div className="text-center py-8 text-gray-500">{t('adminB2b.noPayments')}</div>
      ) : paymentHistory.map((payment, idx) => (
        <div key={payment.id || idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/[0.07] transition-colors"
          data-testid={`payment-row-${idx}`}>
          <div className="md:col-span-2 text-sm text-gray-400">
            {payment.date_vente ? new Date(payment.date_vente).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
          </div>
          <div className="md:col-span-3">
            <p className="text-white text-sm font-medium">{payment.client_nom || '-'}</p>
            <p className="text-xs text-gray-500">{payment.b2b_repair_ref || payment.ref_vente}</p>
          </div>
          <div className="md:col-span-2">
            <span className="text-cyan-400 font-mono text-xs">{payment.b2b_invoice_number || '-'}</span>
          </div>
          <div className="md:col-span-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
              payment.mode_paiement === 'Stripe' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
              payment.mode_paiement === 'CB' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
              payment.mode_paiement === 'Espèces' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
              'bg-gray-500/20 text-gray-400 border border-gray-500/30'
            }`}>{payment.mode_paiement}</span>
          </div>
          <div className="md:col-span-1">
            <span className="text-white font-bold text-sm">{parseFloat(payment.prix_total || 0).toFixed(2)} &euro;</span>
          </div>
          <div className="md:col-span-2">
            {payment.b2b_invoice_id && (
              <button onClick={() => printInvoiceById(payment.b2b_invoice_id)}
                className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 border border-blue-500/30 text-blue-400 rounded-lg text-xs hover:bg-blue-500/30"
                data-testid={`payment-print-${idx}`}>
                <FileText className="w-3 h-3" /> {t('adminB2b.invoice')}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);
